import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
    Shield, Brain, Eye, Scale, Zap, Lock, Globe, Phone,
    ChevronRight, Play, Check, ArrowRight, MessageCircle,
    Server, Mail, AlertTriangle, BarChart3, Clock, Users, X
} from 'lucide-react';
import ChatBot from '@/components/ChatBot';
import { LOGO_URL, BRAND } from '@/lib/brand';

// Demo Video URL
const DEMO_VIDEO_URL = "https://customer-assets.emergentagent.com/job_shirosoc/artifacts/wnt88p10_Video%20Project%202.mp4";

const LandingPage = () => {
    const [activeFeature, setActiveFeature] = useState(0);
    const [isVisible, setIsVisible] = useState({});
    const [isAnnual, setIsAnnual] = useState(false);
    const [isVideoOpen, setIsVideoOpen] = useState(false);

    const features = [
        {
            icon: Eye,
            title: 'Watchdog',
            subtitle: 'Layer 1 - Detection',
            description: 'Lightning-fast anomaly detection scanning millions of logs per second. Identifies threats before they escalate.',
            color: 'cyan'
        },
        {
            icon: Brain,
            title: 'Investigator',
            subtitle: 'Layer 2 - Analysis',
            description: 'Deep contextual investigation using AI. Correlates user behavior, location, and historical patterns.',
            color: 'purple'
        },
        {
            icon: Scale,
            title: 'Jurist',
            subtitle: 'Layer 3 - Compliance',
            description: 'Validates actions against security policies and precedent. Ensures every decision is audit-ready.',
            color: 'green'
        }
    ];

    const benefits = [
        { icon: Clock, title: '24/7 Protection', desc: 'Round-the-clock autonomous security monitoring' },
        { icon: Zap, title: 'Instant Response', desc: 'Millisecond threat detection and response' },
        { icon: Lock, title: 'Zero Trust', desc: 'Every access attempt verified and validated' },
        { icon: BarChart3, title: 'Full Visibility', desc: 'Complete audit trail of all security decisions' },
        { icon: Users, title: 'No Human Error', desc: 'AI-powered decisions without fatigue or bias' },
        { icon: Globe, title: 'Cloud Native', desc: 'Protect Office 365, Google Workspace & more' }
    ];

    const pricingPlans = [
        {
            name: 'Essential',
            monthlyPrice: 8,
            annualPrice: 6.80,
            unit: '/device/mo',
            description: 'Small businesses & retail',
            minDevices: 5,
            features: [
                'AI-powered monitoring 24/7',
                'Automated threat blocking',
                'Weekly PDF security reports',
                'Email alerts only',
                '7-day audit log retention',
                'Basic threat dashboard'
            ],
            cta: 'Start Free Trial',
            popular: false
        },
        {
            name: 'Pro',
            monthlyPrice: 15,
            annualPrice: 12.75,
            unit: '/device/mo',
            description: 'Law firms, agencies & accountants',
            minDevices: 10,
            features: [
                'Everything in Essential +',
                'Voice AI assistant (30 min/mo)',
                'Real-time threat verification',
                '30-day audit log retention',
                'Ransomware rollback protection',
                'Priority email & chat support'
            ],
            cta: 'Start Free Trial',
            popular: true
        },
        {
            name: 'Compliance',
            monthlyPrice: 25,
            annualPrice: 21.25,
            unit: '/device/mo',
            description: 'Healthcare, finance & regulated industries',
            minDevices: 10,
            features: [
                'Everything in Pro +',
                'Unlimited Voice AI assistant',
                '1-year audit log retention',
                'ISO 27001 & GDPR compliance reports',
                'HIPAA-ready security controls',
                'Dedicated account manager'
            ],
            cta: 'Start Free Trial',
            popular: false
        },
        {
            name: 'Enterprise',
            monthlyPrice: null,
            annualPrice: null,
            unit: '',
            description: '100+ devices & custom needs',
            minDevices: 100,
            features: [
                'Everything in Compliance +',
                'Custom AI model training',
                'On-premise deployment option',
                'Unlimited audit retention',
                'Custom integrations & API',
                'SLA guarantees & 24/7 support'
            ],
            cta: 'Contact Sales',
            popular: false
        }
    ];

    const incidentServices = [
        {
            name: 'Security Scan & Report',
            price: '$500',
            description: 'Comprehensive security assessment with detailed findings report'
        },
        {
            name: 'Full Remediation',
            price: '$1,500 - $3,000',
            description: 'Complete cleanup and hardening after a security incident'
        },
        {
            name: 'Emergency Response',
            price: '$5,000+',
            description: '24-hour SLA incident response with dedicated team'
        }
    ];

    return (
        <div className="min-h-screen bg-[#0a0a0f] text-white">
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0f]/80 backdrop-blur-lg border-b border-white/5">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3">
                        <img src={LOGO_URL} alt="Shiro Security" className="w-10 h-10 object-contain" />
                        <div className="flex items-baseline gap-1">
                            <span className="text-xl font-bold gradient-text">SHIRO</span>
                            <span className="text-xl font-light text-white/80">Security</span>
                        </div>
                    </Link>
                    <div className="hidden md:flex items-center gap-8">
                        <a href="#features" className="nav-link text-white/70 hover:text-white">Features</a>
                        <a href="#how-it-works" className="nav-link text-white/70 hover:text-white">How It Works</a>
                        <a href="#pricing" className="nav-link text-white/70 hover:text-white">Pricing</a>
                    </div>
                    <div className="flex items-center gap-4">
                        <Link to="/login">
                            <Button variant="ghost" className="text-white/70 hover:text-white hover:bg-white/5">
                                Sign In
                            </Button>
                        </Link>
                        <Link to="/signup">
                            <Button className="bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-400 hover:to-purple-500 text-white border-0">
                                Get Started
                            </Button>
                        </Link>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="relative pt-32 pb-20 px-6 hero-gradient cyber-grid overflow-hidden">
                <div className="max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div className="space-y-8">
                            <Badge className="bg-cyan-500/10 text-cyan-400 border-cyan-500/20 px-4 py-1">
                                AI-Powered Security
                            </Badge>
                            <h1 className="text-5xl lg:text-6xl font-bold leading-tight">
                                Cybersecurity with
                                <span className="block gradient-text">Zero Human Intervention</span>
                            </h1>
                            <p className="text-xl text-white/60 max-w-xl">
                                Shiro Security uses a revolutionary 3-Layer AI Architecture to detect, investigate, and respond to threats autonomously. Your digital fortress, powered by artificial intelligence.
                            </p>
                            <div className="flex flex-wrap gap-4">
                                <Link to="/signup">
                                    <Button size="lg" className="bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-400 hover:to-purple-500 text-white text-lg px-8 py-6 animate-pulse-glow">
                                        Start Free Trial
                                        <ArrowRight className="ml-2 w-5 h-5" />
                                    </Button>
                                </Link>
                                <Button 
                                    size="lg" 
                                    variant="outline" 
                                    className="border-white/20 text-white hover:bg-white/5 text-lg px-8 py-6"
                                    onClick={() => setIsVideoOpen(true)}
                                    data-testid="watch-demo-btn"
                                >
                                    <Play className="mr-2 w-5 h-5" />
                                    Watch Demo
                                </Button>
                            </div>
                            <div className="flex items-center gap-8 pt-4">
                                <div className="flex items-center gap-2">
                                    <Check className="w-5 h-5 text-green-400" />
                                    <span className="text-white/60">No credit card required</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Check className="w-5 h-5 text-green-400" />
                                    <span className="text-white/60">14-day free trial</span>
                                </div>
                            </div>
                        </div>
                        <div className="relative">
                            {/* Logo Showcase with Glow Effect */}
                            <div className="relative w-full max-w-lg mx-auto">
                                {/* Animated glow background */}
                                <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/30 to-purple-600/30 rounded-full blur-3xl animate-pulse scale-75"></div>
                                <div className="absolute inset-0 bg-gradient-to-tr from-purple-500/20 to-cyan-400/20 rounded-full blur-2xl animate-float"></div>
                                
                                {/* Logo Container */}
                                <div className="relative flex flex-col items-center justify-center p-8">
                                    {/* Main Logo */}
                                    <div className="relative">
                                        <div className="absolute -inset-4 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-full opacity-20 blur-xl animate-pulse"></div>
                                        <img 
                                            src={LOGO_URL} 
                                            alt="Shiro Security - AI Guardian" 
                                            className="relative w-64 h-64 object-contain drop-shadow-2xl animate-float"
                                        />
                                    </div>
                                    
                                    {/* Catchy Quote */}
                                    <div className="mt-8 text-center">
                                        <h2 className="text-2xl lg:text-3xl font-bold mb-3">
                                            <span className="gradient-text">The 3-Layer Defense</span>
                                            <span className="text-white"> for Modern Apps</span>
                                        </h2>
                                        <p className="text-lg text-white/60 font-light tracking-wide">
                                            Automated. AI-Driven. Self-Service Security.
                                        </p>
                                    </div>

                                    {/* Status Indicators */}
                                    <div className="mt-8 grid grid-cols-3 gap-4 w-full max-w-md">
                                        <div className="glass-card rounded-xl p-4 text-center">
                                            <div className="text-2xl font-bold text-cyan-400">2.4M</div>
                                            <div className="text-xs text-white/40">Threats Blocked</div>
                                        </div>
                                        <div className="glass-card rounded-xl p-4 text-center">
                                            <div className="text-2xl font-bold text-purple-400">99.9%</div>
                                            <div className="text-xs text-white/40">Uptime</div>
                                        </div>
                                        <div className="glass-card rounded-xl p-4 text-center">
                                            <div className="text-2xl font-bold text-green-400">0</div>
                                            <div className="text-xs text-white/40">Breaches</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* AI Layers Status Banner */}
            <section className="py-6 bg-gradient-to-r from-cyan-500/10 via-purple-500/10 to-green-500/10 border-y border-white/5">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
                        <div className="flex items-center gap-3">
                            <div className="w-3 h-3 rounded-full bg-cyan-400 animate-pulse shadow-lg shadow-cyan-400/50"></div>
                            <Eye className="w-5 h-5 text-cyan-400" />
                            <span className="text-white/80 font-medium">Watchdog Active</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="w-3 h-3 rounded-full bg-purple-400 animate-pulse shadow-lg shadow-purple-400/50"></div>
                            <Brain className="w-5 h-5 text-purple-400" />
                            <span className="text-white/80 font-medium">Investigator Ready</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="w-3 h-3 rounded-full bg-green-400 animate-pulse shadow-lg shadow-green-400/50"></div>
                            <Scale className="w-5 h-5 text-green-400" />
                            <span className="text-white/80 font-medium">Jurist Online</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Trusted By */}
            <section className="py-12 border-b border-white/5 bg-white/[0.02]">
                <div className="max-w-7xl mx-auto px-6">
                    <p className="text-center text-white/40 mb-8">Trusted by security-conscious organizations worldwide</p>
                    <div className="flex flex-wrap justify-center items-center gap-12 opacity-50">
                        {['TechCorp', 'SecureBank', 'HealthPlus', 'GlobalRetail', 'DataDriven'].map((company) => (
                            <div key={company} className="text-xl font-bold text-white/30">{company}</div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section id="features" className="py-24 px-6">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <Badge className="bg-purple-500/10 text-purple-400 border-purple-500/20 mb-4">The 3-Layer Brain</Badge>
                        <h2 className="text-4xl font-bold mb-4">AI Architecture That Thinks Like a SOC Team</h2>
                        <p className="text-xl text-white/60 max-w-2xl mx-auto">
                            Our revolutionary 3-Layer AI system replicates the entire hierarchy of a Security Operations Center, working 24/7 without breaks.
                        </p>
                    </div>

                    <div className="grid lg:grid-cols-3 gap-8">
                        {features.map((feature, index) => (
                            <Card
                                key={index}
                                className={`glass-card border-0 overflow-hidden group cursor-pointer transition-all duration-300 ${
                                    activeFeature === index ? 'ring-2 ring-cyan-500/50' : ''
                                }`}
                                onClick={() => setActiveFeature(index)}
                            >
                                <CardContent className="p-8">
                                    <div className={`w-16 h-16 rounded-2xl mb-6 flex items-center justify-center ${
                                        feature.color === 'cyan' ? 'bg-cyan-500/20' :
                                        feature.color === 'purple' ? 'bg-purple-500/20' : 'bg-green-500/20'
                                    }`}>
                                        <feature.icon className={`w-8 h-8 ${
                                            feature.color === 'cyan' ? 'text-cyan-400' :
                                            feature.color === 'purple' ? 'text-purple-400' : 'text-green-400'
                                        }`} />
                                    </div>
                                    <div className="text-sm text-white/40 mb-2">{feature.subtitle}</div>
                                    <h3 className="text-2xl font-bold mb-4">{feature.title}</h3>
                                    <p className="text-white/60">{feature.description}</p>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* How It Works - Agent Installation */}
            <section id="how-it-works" className="py-24 px-6 bg-gradient-to-b from-transparent via-purple-900/10 to-transparent">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <Badge className="bg-cyan-500/10 text-cyan-400 border-cyan-500/20 mb-4">How It Works</Badge>
                        <h2 className="text-4xl font-bold mb-4">Get Protected in 3 Simple Steps</h2>
                        <p className="text-xl text-white/60 max-w-2xl mx-auto">
                            Deploy our lightweight agent in minutes. No complex setup, no IT team required.
                        </p>
                    </div>

                    <div className="grid lg:grid-cols-3 gap-8 mb-16">
                        {[
                            { 
                                step: '01', 
                                title: 'Sign Up & Choose Plan', 
                                desc: 'Create your account and select the plan that fits your needs. Start with a 14-day free trial.',
                                icon: Users,
                                color: 'cyan',
                                details: ['No credit card required', 'Instant account activation', 'Choose from 4 flexible plans']
                            },
                            { 
                                step: '02', 
                                title: 'Download the Agent', 
                                desc: 'Get our lightweight security agent (~5MB) for Windows, Linux, or macOS from your dashboard.',
                                icon: Server,
                                color: 'purple',
                                details: ['Only ~5MB download', 'Single binary, no dependencies', 'Windows, Linux & macOS']
                            },
                            { 
                                step: '03', 
                                title: 'You\'re Protected', 
                                desc: 'Run the installer and our 3-Layer AI immediately starts monitoring and protecting your devices.',
                                icon: Shield,
                                color: 'green',
                                details: ['< 1% CPU usage', 'Real-time threat detection', '24/7 AI monitoring']
                            }
                        ].map((item, index) => (
                            <div key={index} className="relative group">
                                {/* Connecting line */}
                                {index < 2 && (
                                    <div className="hidden lg:block absolute top-20 left-[60%] w-full h-[2px] bg-gradient-to-r from-cyan-500/50 to-transparent z-0"></div>
                                )}
                                
                                <div className="glass-card rounded-2xl p-8 relative z-10 h-full hover:bg-white/5 transition-all duration-300 hover:scale-105">
                                    {/* Step Number */}
                                    <div className={`absolute -top-4 -left-2 text-7xl font-bold ${
                                        item.color === 'cyan' ? 'text-cyan-500/10' :
                                        item.color === 'purple' ? 'text-purple-500/10' : 'text-green-500/10'
                                    }`}>
                                        {item.step}
                                    </div>
                                    
                                    {/* Icon */}
                                    <div className={`w-16 h-16 rounded-2xl mb-6 flex items-center justify-center ${
                                        item.color === 'cyan' ? 'bg-cyan-500/20' :
                                        item.color === 'purple' ? 'bg-purple-500/20' : 'bg-green-500/20'
                                    }`}>
                                        <item.icon className={`w-8 h-8 ${
                                            item.color === 'cyan' ? 'text-cyan-400' :
                                            item.color === 'purple' ? 'text-purple-400' : 'text-green-400'
                                        }`} />
                                    </div>
                                    
                                    <h3 className="text-2xl font-bold mb-3">{item.title}</h3>
                                    <p className="text-white/60 mb-6">{item.desc}</p>
                                    
                                    {/* Details List */}
                                    <ul className="space-y-2">
                                        {item.details.map((detail, i) => (
                                            <li key={i} className="flex items-center gap-2 text-sm text-white/50">
                                                <Check className={`w-4 h-4 ${
                                                    item.color === 'cyan' ? 'text-cyan-400' :
                                                    item.color === 'purple' ? 'text-purple-400' : 'text-green-400'
                                                }`} />
                                                {detail}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Agent Features Highlight */}
                    <div className="glass-card rounded-3xl p-8 lg:p-12">
                        <div className="grid lg:grid-cols-2 gap-12 items-center">
                            <div>
                                <h3 className="text-3xl font-bold mb-4">
                                    The Lightest Security Agent
                                    <span className="block gradient-text">You'll Ever Install</span>
                                </h3>
                                <p className="text-white/60 mb-8">
                                    Our "dumb collector" design keeps the agent ultra-lightweight. All the heavy lifting happens in the cloud where our 3-Layer AI analyzes threats in real-time.
                                </p>
                                
                                <div className="grid grid-cols-2 gap-6 mb-8">
                                    <div className="text-center p-4 rounded-xl bg-white/5">
                                        <div className="text-3xl font-bold text-cyan-400 mb-1">~5MB</div>
                                        <div className="text-xs text-white/40">Download Size</div>
                                    </div>
                                    <div className="text-center p-4 rounded-xl bg-white/5">
                                        <div className="text-3xl font-bold text-purple-400 mb-1">&lt;1%</div>
                                        <div className="text-xs text-white/40">CPU Usage</div>
                                    </div>
                                    <div className="text-center p-4 rounded-xl bg-white/5">
                                        <div className="text-3xl font-bold text-green-400 mb-1">&lt;50MB</div>
                                        <div className="text-xs text-white/40">RAM Usage</div>
                                    </div>
                                    <div className="text-center p-4 rounded-xl bg-white/5">
                                        <div className="text-3xl font-bold text-amber-400 mb-1">TLS 1.3</div>
                                        <div className="text-xs text-white/40">Encryption</div>
                                    </div>
                                </div>

                                <Link to="/signup">
                                    <Button size="lg" className="bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-400 hover:to-purple-500 text-white">
                                        Start Free Trial
                                        <ArrowRight className="ml-2 w-5 h-5" />
                                    </Button>
                                </Link>
                            </div>
                            
                            <div className="space-y-4">
                                <h4 className="font-semibold text-white/80 mb-4">What the Agent Monitors:</h4>
                                {[
                                    { icon: Eye, title: 'Process Activity', desc: 'Tracks process creation, termination, and suspicious behavior' },
                                    { icon: Globe, title: 'Network Connections', desc: 'Monitors all inbound/outbound connections and flags anomalies' },
                                    { icon: Lock, title: 'File Integrity', desc: 'Detects unauthorized changes to critical system files' },
                                    { icon: BarChart3, title: 'Resource Usage', desc: 'Spots crypto-mining and other resource-abuse attacks' },
                                    { icon: AlertTriangle, title: 'Security Events', desc: 'Captures login attempts, privilege escalations & more' }
                                ].map((feature, index) => (
                                    <div key={index} className="flex items-start gap-4 p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors">
                                        <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                                            <feature.icon className="w-5 h-5 text-cyan-400" />
                                        </div>
                                        <div>
                                            <h5 className="font-semibold text-white">{feature.title}</h5>
                                            <p className="text-sm text-white/50">{feature.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Benefits Grid */}
            <section className="py-24 px-6">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-bold mb-4">Why Choose Shiro Security?</h2>
                        <p className="text-xl text-white/60">Enterprise-grade protection without enterprise complexity</p>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {benefits.map((benefit, index) => (
                            <div key={index} className="glass-card rounded-xl p-6 flex items-start gap-4 group hover:bg-white/5 transition-colors">
                                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500/20 to-purple-500/20 flex items-center justify-center flex-shrink-0">
                                    <benefit.icon className="w-6 h-6 text-cyan-400" />
                                </div>
                                <div>
                                    <h3 className="font-bold mb-1">{benefit.title}</h3>
                                    <p className="text-sm text-white/60">{benefit.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Pricing Section */}
            <section id="pricing" className="py-24 px-6 bg-gradient-to-b from-transparent via-cyan-900/10 to-transparent">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-12">
                        <Badge className="bg-green-500/10 text-green-400 border-green-500/20 mb-4">Pricing</Badge>
                        <h2 className="text-4xl font-bold mb-4">Simple Per-Device Pricing</h2>
                        <p className="text-xl text-white/60 mb-8">Pay only for what you protect. No hidden fees.</p>
                        
                        {/* Billing Toggle */}
                        <div className="flex items-center justify-center gap-4 mb-8">
                            <span className={`text-sm ${!isAnnual ? 'text-white' : 'text-white/50'}`}>Monthly</span>
                            <button
                                onClick={() => setIsAnnual(!isAnnual)}
                                className={`relative w-14 h-7 rounded-full transition-colors ${isAnnual ? 'bg-gradient-to-r from-cyan-500 to-purple-600' : 'bg-white/20'}`}
                                data-testid="billing-toggle"
                            >
                                <span className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${isAnnual ? 'translate-x-8' : 'translate-x-1'}`}></span>
                            </button>
                            <span className={`text-sm ${isAnnual ? 'text-white' : 'text-white/50'}`}>
                                Annual <span className="text-green-400 font-bold">(Save 15%)</span>
                            </span>
                        </div>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {pricingPlans.map((plan, index) => (
                            <Card key={index} className={`glass-card border-0 overflow-hidden relative ${
                                plan.popular ? 'ring-2 ring-cyan-500 scale-105' : ''
                            }`}>
                                {plan.popular && (
                                    <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-cyan-500 to-purple-600 text-white text-xs font-bold py-2 text-center">
                                        RECOMMENDED
                                    </div>
                                )}
                                <CardContent className={`p-6 ${plan.popular ? 'pt-12' : ''}`}>
                                    <h3 className="text-xl font-bold mb-1">{plan.name}</h3>
                                    <p className="text-white/50 text-xs mb-4">{plan.description}</p>
                                    
                                    <div className="mb-4">
                                        {plan.monthlyPrice ? (
                                            <>
                                                <span className="text-4xl font-bold">
                                                    ${isAnnual ? plan.annualPrice.toFixed(2) : plan.monthlyPrice}
                                                </span>
                                                <span className="text-white/60 text-sm">{plan.unit}</span>
                                                {isAnnual && (
                                                    <div className="text-green-400 text-xs mt-1">
                                                        Save ${((plan.monthlyPrice - plan.annualPrice) * 12).toFixed(0)}/device/year
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <span className="text-3xl font-bold">Custom</span>
                                        )}
                                    </div>
                                    
                                    <div className="text-xs text-white/40 mb-4 pb-4 border-b border-white/10">
                                        Min. {plan.minDevices} devices
                                    </div>
                                    
                                    <ul className="space-y-2 mb-6">
                                        {plan.features.map((feature, i) => (
                                            <li key={i} className="flex items-start gap-2 text-xs">
                                                <Check className="w-3 h-3 text-green-400 flex-shrink-0 mt-0.5" />
                                                <span className="text-white/70">{feature}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    
                                    <Link to="/signup">
                                        <Button className={`w-full text-sm ${plan.popular 
                                            ? 'bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-400 hover:to-purple-500 text-white' 
                                            : 'bg-white/10 hover:bg-white/20 text-white'
                                        }`}>
                                            {plan.cta}
                                        </Button>
                                    </Link>
                                </CardContent>
                            </Card>
                        ))}
                    </div>

                    {/* Incident Response Services */}
                    <div className="mt-16">
                        <div className="text-center mb-8">
                            <h3 className="text-2xl font-bold mb-2">Incident Response Services</h3>
                            <p className="text-white/60">Already been breached? We can help.</p>
                        </div>
                        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
                            {incidentServices.map((service, index) => (
                                <div key={index} className="glass-card rounded-xl p-6 text-center">
                                    <div className="text-2xl font-bold text-cyan-400 mb-2">{service.price}</div>
                                    <div className="font-medium mb-2">{service.name}</div>
                                    <p className="text-xs text-white/50">{service.description}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-24 px-6">
                <div className="max-w-4xl mx-auto text-center">
                    <div className="glass-card rounded-3xl p-12 relative overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 to-purple-600/10"></div>
                        <div className="relative z-10">
                            <h2 className="text-4xl font-bold mb-4">Ready to Secure Your Future?</h2>
                            <p className="text-xl text-white/60 mb-8">Join thousands of organizations trusting Shiro Security to protect their digital assets.</p>
                            <Link to="/signup">
                                <Button size="lg" className="bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-400 hover:to-purple-500 text-white text-lg px-12 py-6">
                                    Start Your Free Trial
                                    <ArrowRight className="ml-2 w-5 h-5" />
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-12 px-6 border-t border-white/5">
                <div className="max-w-7xl mx-auto">
                    <div className="grid md:grid-cols-4 gap-12">
                        <div>
                            <div className="flex items-center gap-3 mb-4">
                                <img src={LOGO_URL} alt="Shiro Security" className="w-10 h-10 object-contain" />
                                <span className="text-lg font-bold">SHIRO Security</span>
                            </div>
                            <p className="text-white/60 text-sm mb-4">AI-powered cybersecurity with zero human intervention. Protecting your digital assets 24/7.</p>
                            <p className="text-white/40 text-xs italic mb-4">"The 3-Layer Defense for Modern Apps"</p>
                            {/* Social Media Links */}
                            <div className="flex items-center gap-3">
                                <a href="https://www.facebook.com/share/1Dxms3nSqG/" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-white/5 hover:bg-cyan-500/20 flex items-center justify-center transition-colors group" title="Facebook">
                                    <svg className="w-4 h-4 text-white/60 group-hover:text-cyan-400" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                                </a>
                                <a href="https://www.linkedin.com/company/shiro-security/" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-white/5 hover:bg-cyan-500/20 flex items-center justify-center transition-colors group" title="LinkedIn">
                                    <svg className="w-4 h-4 text-white/60 group-hover:text-cyan-400" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                                </a>
                                <a href="http://tiktok.com/@shirosec2" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-white/5 hover:bg-cyan-500/20 flex items-center justify-center transition-colors group" title="TikTok">
                                    <svg className="w-4 h-4 text-white/60 group-hover:text-cyan-400" fill="currentColor" viewBox="0 0 24 24"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
                                </a>
                                <a href="https://www.instagram.com/shirosec/" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-white/5 hover:bg-cyan-500/20 flex items-center justify-center transition-colors group" title="Instagram">
                                    <svg className="w-4 h-4 text-white/60 group-hover:text-cyan-400" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                                </a>
                                <a href="https://x.com/shirosec192725" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-white/5 hover:bg-cyan-500/20 flex items-center justify-center transition-colors group" title="X (Twitter)">
                                    <svg className="w-4 h-4 text-white/60 group-hover:text-cyan-400" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                                </a>
                            </div>
                        </div>
                        <div>
                            <h4 className="font-bold mb-4">Product</h4>
                            <ul className="space-y-2 text-sm text-white/60">
                                <li><a href="#features" className="hover:text-white">Features</a></li>
                                <li><a href="#pricing" className="hover:text-white">Pricing</a></li>
                                <li><a href="#how-it-works" className="hover:text-white">How It Works</a></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-bold mb-4">Company</h4>
                            <ul className="space-y-2 text-sm text-white/60">
                                <li><a href="#" className="hover:text-white">About Us</a></li>
                                <li><a href="#" className="hover:text-white">Careers</a></li>
                                <li><a href="mailto:support@shirosec.com" className="hover:text-white">Contact</a></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-bold mb-4">Contact Us</h4>
                            <ul className="space-y-3 text-sm text-white/60">
                                <li className="flex items-start gap-2">
                                    <svg className="w-4 h-4 mt-0.5 text-cyan-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                    <span>131 Continental Dr, Suite 305<br/>Newark, DE 19713, USA</span>
                                </li>
                                <li className="flex items-center gap-2">
                                    <svg className="w-4 h-4 text-cyan-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                                    <a href="tel:+13025202688" className="hover:text-white">+1 (302) 520-2688</a>
                                </li>
                                <li className="flex items-center gap-2">
                                    <svg className="w-4 h-4 text-cyan-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                                    <a href="tel:+96176388467" className="hover:text-white">+961 76 388 467</a>
                                </li>
                                <li className="flex items-center gap-2">
                                    <svg className="w-4 h-4 text-cyan-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                                    <a href="mailto:support@shirosec.com" className="hover:text-white">support@shirosec.com</a>
                                </li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-bold mb-4">Legal</h4>
                            <ul className="space-y-2 text-sm text-white/60">
                                <li><Link to="/privacy" className="hover:text-white">Privacy Policy</Link></li>
                                <li><Link to="/terms" className="hover:text-white">Terms of Service</Link></li>
                                <li><Link to="/security" className="hover:text-white">Security</Link></li>
                            </ul>
                        </div>
                    </div>
                    <div className="mt-12 pt-8 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-4">
                        <div className="text-white/40 text-sm">
                            &copy; {new Date().getFullYear()} Shiro Security, LLC. All rights reserved.
                        </div>
                        <div className="text-white/30 text-xs">
                            Automated. AI-Driven. Self-Service Security.
                        </div>
                    </div>
                </div>
            </footer>

            {/* Video Modal */}
            {isVideoOpen && (
                <div 
                    className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm"
                    onClick={() => setIsVideoOpen(false)}
                >
                    <div 
                        className="relative w-full max-w-5xl mx-4"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Video Container */}
                        <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-cyan-500/20">
                            {/* Close Button - Top Right Corner */}
                            <button
                                onClick={() => setIsVideoOpen(false)}
                                className="absolute top-3 right-3 z-20 bg-black/70 hover:bg-black text-white/80 hover:text-white transition-all rounded-full p-2"
                                data-testid="close-video-btn"
                            >
                                <X className="w-6 h-6" />
                            </button>
                            
                            {/* Video Header */}
                            <div className="bg-gradient-to-r from-cyan-500/10 to-purple-500/10 px-6 py-4 border-b border-white/10">
                                <div className="flex items-center gap-3">
                                    <img src={LOGO_URL} alt="Shiro Security" className="w-8 h-8 object-contain" />
                                    <div>
                                        <h3 className="font-bold text-white">Shiro Security Demo</h3>
                                        <p className="text-xs text-white/50">The 3-Layer Defense for Modern Apps</p>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Video Player */}
                            <div className="bg-black aspect-video">
                                <video
                                    src={DEMO_VIDEO_URL}
                                    controls
                                    autoPlay
                                    poster="https://customer-assets.emergentagent.com/job_5ae6a46c-eb47-41b3-a183-2eea4382aa99/artifacts/nlucqggl_2150078.jpg"
                                    preload="auto"
                                    className="w-full h-full"
                                    data-testid="demo-video"
                                >
                                    Your browser does not support the video tag.
                                </video>
                            </div>
                            
                            {/* Video Footer */}
                            <div className="bg-[#0a0a0f] px-6 py-4 flex items-center justify-between">
                                <p className="text-white/50 text-sm">80 seconds | AI-Powered Cybersecurity</p>
                                <Link to="/signup" onClick={() => setIsVideoOpen(false)}>
                                    <Button className="bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-400 hover:to-purple-500 text-white">
                                        Start Free Trial
                                        <ArrowRight className="ml-2 w-4 h-4" />
                                    </Button>
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Chatbot */}
            <ChatBot />
        </div>
    );
};

export default LandingPage;
